import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {IdName} from '@domain/id-name';
import {MachineType} from '@domain/machine/machine-type.enum';
import {PlasticsMachineType} from '@domain/machine/plastics-machine-type';
import {Permission} from '@domain/profile/permission.enum';
import {MatchingCriteria} from '@domain/utilities/tool/matching-criteria';
import {SelectMachineTypeService} from '@presentation/pages/machine-overview/add-equipment-kind/generic-equipment-kind-form/select-machine-type/select-machine-type.service';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {OverviewMachineGroup} from '@presentation/pages/machine-overview/overview-machine-group';
import {MachineOverviewService} from '@presentation/pages/machine-overview/overview-machines-views/overview-machines-list/machine-overview.service';
import {PlasticMachineOverviewList} from '@presentation/pages/machine-overview/plastic-machine-overview-list';
import {
  AlertDialogResult,
  AssertionUtils,
  BaseComponent,
  CrudObjectSelectionDialogComponent,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  NoDataOverlayComponentParams,
  OverlayActionsService,
  OverlayComponentParams,
  OverlayComponentParamsAction,
  TranslateService
} from '@vdw/angular-component-library';
import {ColDef, EditableCallbackParams, GetRowIdParams, GridApi, GridReadyEvent, IRowDragItem, IRowNode} from 'ag-grid-community';
import {filter, takeUntil} from 'rxjs';
import {MatchingCriteriaData} from './matching-criteria-data.interface';
import {MatchingCriteriaOverviewService} from './matching-criteria-overview.service';

type CrudOverviewDataConfigType = MatchingCriteria | MachineOverviewList | PlasticsMachineType | OverviewMachineGroup;

@Component({
  selector: 'app-matching-criteria',
  styleUrls: ['./matching-criteria.component.scss'],
  templateUrl: './matching-criteria.component.html'
})
export class MatchingCriteriaComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public form: FormGroup;
  @Input() public matchingCriteria: MatchingCriteria[];
  @Input() public editPermission: Permission;
  @Input() public enableDragHandle: boolean = false;
  @Input() public enableMultiRowSelect: boolean = true;
  @Input() public showStandardEfficiencyColumn: boolean = false;
  @Input() public showSpeedColumn: boolean = false;
  @Input() public showMachineGroup: boolean = false;
  @Input() public canEdit: boolean = true;
  @Output() public matchingCriteriaChange = new EventEmitter<MatchingCriteria[]>();

  public isLoading = true;
  public config: CrudOverviewDataConfig<CrudOverviewDataConfigType>;
  public plasticMachineTypes: PlasticsMachineType[] = [];
  public machineGroups: OverviewMachineGroup[];

  private readonly TRANSLATION_KEY = 'UTILITIES.TOOL.MATCHING_CRITERIA.MATCHING_CRITERIA';
  private readonly noOverlayAction: OverlayComponentParamsAction[] = [
    {
      key: 'addMachine',
      titleKey: this.translate.instant('GENERAL.ACTIONS.ADD_OBJECT', {object: this.translate.instant('MACHINE.MACHINE', {count: 1}).toLowerCase()}),
      isPrimary: true
    }
  ];

  private configGridApi: GridApi;
  private machineConfig: CrudOverviewDataConfig<CrudOverviewDataConfigType>;
  private plasticMachineTypeConfig: CrudOverviewDataConfig<CrudOverviewDataConfigType>;
  private machineGroupConfig: CrudOverviewDataConfig<CrudOverviewDataConfigType>;
  private plasticMachines: MachineOverviewList[] = [];

  public constructor(
    private readonly selectMachineTypeService: SelectMachineTypeService,
    private readonly overlayActionsService: OverlayActionsService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<CrudOverviewDataConfigType>,
    private readonly matchingCriteriaOverviewService: MatchingCriteriaOverviewService,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly machineOverviewService: MachineOverviewService
  ) {
    super();
  }

  @Input() public set matchingcriteriaData(data: MatchingCriteriaData) {
    this.plasticMachines = data.machines;
    this.setMachineConfig(this.plasticMachines);

    if (this.showMachineGroup) {
      this.machineGroups = data.machineGroups;
      this.setMachineGroupConfig(this.machineGroups);
    } else {
      this.plasticMachineTypes = data.machineTypes;
      this.setMachineTypeConfig(this.plasticMachineTypes);
    }

    if (!AssertionUtils.isEmpty(this.plasticMachines)) {
      this.isLoading = false;
      this.configGridApi?.setGridOption('loading', false);
    }
  }

  public ngOnInit(): void {
    this.subscribeToOverlayActions();
    this.setDisabledForOverlay();

    this.setConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('matchingCriteria' in changes && !changes.matchingCriteria.isFirstChange()) {
      this.configGridApi.setGridOption('rowData', changes.matchingCriteria.currentValue);
      this.configGridApi.redrawRows();
      this.config.rowData = changes.matchingCriteria.currentValue;
    }

    if ('canEdit' in changes && !changes.canEdit.isFirstChange()) {
      this.setDisabledForOverlay();
    }
  }

  public selectMachine(): void {
    const selectedMatchingCriteriaWithMachines = [];
    this.configGridApi.forEachNode((node: IRowNode) => {
      if (node.data.machine !== null) {
        selectedMatchingCriteriaWithMachines.push(node);
      }
    });

    const selectedMachines = selectedMatchingCriteriaWithMachines?.map((node: IRowNode) => node.data.machine);
    this.machineConfig.selectedObjects = selectedMachines;

    this.navigationHelper
      .navigateWithContentSwitcher<PlasticMachineOverviewList[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.machineConfig
          }
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machines: PlasticMachineOverviewList[]) => {
        if (!AssertionUtils.isNullOrUndefined(machines)) {
          const existingSelectedMachineIds = selectedMachines.map((machine: PlasticMachineOverviewList) => machine.id);
          const itemsToAdd = machines.filter((machine: PlasticMachineOverviewList) => !existingSelectedMachineIds.includes(machine.id));
          const newSelectedMachineIds = machines.map((machine: PlasticMachineOverviewList) => machine.id);
          const itemsToRemove = selectedMatchingCriteriaWithMachines
            .map((node: IRowNode) => node.data)
            .filter((matchingCriteria: MatchingCriteria) => !matchingCriteria.isLocked && !newSelectedMachineIds.includes(matchingCriteria.machine.id));

          this.configGridApi.applyTransaction({
            add: itemsToAdd.map(
              (machine: PlasticMachineOverviewList) =>
                new MatchingCriteria(
                  null,
                  new IdName(machine.id, machine.name),
                  null,
                  null,
                  null,
                  null,
                  null,
                  machine.standardSpeed,
                  machine.standardEfficiency,
                  machine.plasticsMachineType?.standardSpeed
                )
            ),
            remove: itemsToRemove
          });

          this.setLatestMatchingCriteriaList();
        }
      });
  }

  public selectMachineType(): void {
    const selectedMatchingCriteriaWithMachineTypes = [];
    this.configGridApi.forEachNode((node: IRowNode) => {
      if (node.data.machineType !== null) {
        selectedMatchingCriteriaWithMachineTypes.push(node);
      }
    });

    const selectedMachineTypes = selectedMatchingCriteriaWithMachineTypes?.map((node: IRowNode) => node.data.machineType);
    this.plasticMachineTypeConfig.selectedObjects = selectedMachineTypes;

    this.navigationHelper
      .navigateWithContentSwitcher<PlasticsMachineType[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.plasticMachineTypeConfig
          }
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machineTypes: PlasticsMachineType[]) => {
        if (!AssertionUtils.isNullOrUndefined(machineTypes)) {
          const existingSelectedMachineTypeIds = selectedMachineTypes.map((machineType: PlasticsMachineType) => machineType.id);
          const itemsToAdd = machineTypes.filter((machineType: PlasticsMachineType) => !existingSelectedMachineTypeIds.includes(machineType.id));
          const newSelectedMachineTypeIds = machineTypes.map((machineType: PlasticsMachineType) => machineType.id);
          const itemsToRemove = selectedMatchingCriteriaWithMachineTypes
            .map((node: IRowNode) => node.data)
            .filter((matchingCriteria: MatchingCriteria) => !matchingCriteria.isLocked && !newSelectedMachineTypeIds.includes(matchingCriteria.machineType.id));
          this.configGridApi.applyTransaction({
            add: itemsToAdd.map(
              (machineType: PlasticsMachineType) => new MatchingCriteria(null, null, new IdName(machineType.id, machineType.name), null, null, null, null, null, null, machineType.standardSpeed)
            ),
            remove: itemsToRemove
          });

          this.setLatestMatchingCriteriaList();
        }
      });
  }

  public selectMachineGroup(): void {
    const selectedMatchingCriteriaWithMachineGroups = [];
    this.configGridApi.forEachNode((node: IRowNode) => {
      if (node.data.machineGroup !== null) {
        selectedMatchingCriteriaWithMachineGroups.push(node);
      }
    });

    const selectedMachineGroups = selectedMatchingCriteriaWithMachineGroups?.map((node: IRowNode) => node.data.machineGroup);
    this.machineGroupConfig.selectedObjects = selectedMachineGroups;

    this.navigationHelper
      .navigateWithContentSwitcher<OverviewMachineGroup[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config: this.machineGroupConfig
          }
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machineGroups: OverviewMachineGroup[]) => {
        if (!AssertionUtils.isNullOrUndefined(machineGroups)) {
          const existingSelectedMachineGroupIds = selectedMachineGroups.map((machineGroup: OverviewMachineGroup) => machineGroup.id);
          const itemsToAdd = machineGroups.filter((machineGroup: OverviewMachineGroup) => !existingSelectedMachineGroupIds.includes(machineGroup.id));
          const newSelectedMachineGroupIds = machineGroups.map((machineGroup: OverviewMachineGroup) => machineGroup.id);
          const itemsToRemove = selectedMatchingCriteriaWithMachineGroups
            .map((node: IRowNode) => node.data)
            .filter((matchingCriteria: MatchingCriteria) => !newSelectedMachineGroupIds.includes(matchingCriteria.machineGroup.id));
          this.configGridApi.applyTransaction({
            add: itemsToAdd.map((machineGroup: OverviewMachineGroup) => new MatchingCriteria(null, null, null, new IdName(machineGroup.id, machineGroup.name), null, null, null)),
            remove: itemsToRemove
          });

          this.setLatestMatchingCriteriaList();
        }
      });
  }

  private subscribeToOverlayActions(): void {
    this.noOverlayAction.push({
      key: this.showMachineGroup ? 'addMachineGroup' : 'addMachineType',
      titleKey: this.translate.instant('GENERAL.ACTIONS.ADD_OBJECT', {object: this.translate.instant(this.showMachineGroup ? 'PLANNING.MACHINE_GROUP' : 'MACHINE.DETAILS.MACHINE_TYPE').toLowerCase()}),
      isPrimary: true
    });

    this.overlayActionsService.actionTriggeredEmitter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((actionKey: string) => {
      if (AssertionUtils.isEmpty(this.plasticMachines)) {
        this.isLoading = true;
        this.configGridApi?.setGridOption('loading', true);
        return;
      }

      if (actionKey === this.noOverlayAction[0].key) {
        this.selectMachine();
      } else if (actionKey === this.noOverlayAction[1].key) {
        this.showMachineGroup ? this.selectMachineGroup() : this.selectMachineType();
      }
    });
  }

  private getColumnDefs(): ColDef[] {
    return this.matchingCriteriaOverviewService.getColumnDefs(
      this.showStandardEfficiencyColumn,
      this.showSpeedColumn,
      (params: EditableCallbackParams): boolean => this.canEdit && !params.data.isLocked
    );
  }

  private setConfig(): void {
    const builderFactory = this.builderFactoryService.getBuilder();

    builderFactory
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: this.editPermission,
          withDelete: (grid: GridButtonClickedEvent) => this.delete(grid),
          allowDeleteMultiple: true
        } as CrudActions)
      })
      .withGridOptions(this.getColumnDefs(), GridIdentifier.MATCHING_CRITERIA, this.matchingCriteria, (builder: GridOptionsBuilder) => {
        builder
          .withOnGridReady((params: GridReadyEvent) => {
            this.configGridApi = params.api;

            if (this.isLoading) {
              params.api.setGridOption('loading', true);
            }
          })
          .withOnFirstDataRendered(() => this.configGridApi.setGridOption('loading', false))
          .withRowSelection(this.enableMultiRowSelect)
          .withGetRowId((params: GetRowIdParams) => this.matchingCriteriaOverviewService.getResource(params))
          .withOnCellValueChanged(() => this.onCellValueChanged())
          .withLockUsedItem((data: MatchingCriteria) => !this.canEdit || data.isLocked)
          .withPopupParent(document.querySelector('.mat-mdc-dialog-container'))
          .withLoadingOverlay({
            scale: 0.7
          } as OverlayComponentParams)
          .withNoRowsOverlay({
            scale: 1,
            titleParam: this.translate.instant(this.TRANSLATION_KEY),
            hideDescription: false,
            actions: this.noOverlayAction
          } as NoDataOverlayComponentParams);

        if (this.enableDragHandle) {
          builder.withRowDraggingInsideGrid((params: IRowDragItem) => params.rowNode.data?.machine.name).withOnRowDragEnd(() => this.setLatestMatchingCriteriaList());
        }

        return builder.build();
      });

    this.config = builderFactory.build();
  }

  private setMachineConfig(machines: MachineOverviewList[]): void {
    this.machineConfig = this.builderFactoryService
      .getBuilder()
      .withEntityName('MACHINE.MACHINE')
      .withGridOptions(this.getColDefForMachine(), GridIdentifier.SELECT_MACHINE, machines, (builder: GridOptionsBuilder) =>
        builder
          .withRowSelection(true)
          .withDisableDeselection((id: number) => this.matchingCriteria.find((matchingCriteria: MatchingCriteria) => matchingCriteria.machine?.id === id)?.isLocked)
          .build()
      )
      .build();
  }

  private setMachineGroupConfig(machinesGroups: OverviewMachineGroup[]): void {
    this.machineGroupConfig = this.builderFactoryService
      .getBuilder()
      .withEntityName('PLANNING.MACHINE_GROUP')
      .withGridOptions(this.getColDefForMachineGroup(), GridIdentifier.SELECT_MACHINE_GROUP, machinesGroups, (builder: GridOptionsBuilder) => builder.withRowSelection(true).build())
      .build();
  }

  private getColDefForMachine(): ColDef[] {
    return this.machineOverviewService.getColumnDefsForSpeed();
  }

  private getColDefForMachineGroup(): ColDef[] {
    return this.matchingCriteriaOverviewService.getColDefForMachineGroup();
  }

  private setMachineTypeConfig(machineTypes: PlasticsMachineType[]): void {
    this.plasticMachineTypeConfig = this.builderFactoryService
      .getBuilder()
      .withEntityName('MACHINE.DETAILS.MACHINE_TYPE')
      .withGridOptions(
        this.selectMachineTypeService.getColumnDefsForMachineType(MachineType.PLASTIC_MACHINE),
        this.selectMachineTypeService.getMachineTypeGridIdentifier(MachineType.PLASTIC_MACHINE),
        machineTypes,
        (builder: GridOptionsBuilder) =>
          builder
            .withRowSelection(true)
            .withDisableDeselection((id: number) => this.matchingCriteria.find((matchingCriteria: MatchingCriteria) => matchingCriteria.machineType?.id === id)?.isLocked)
            .build()
      )
      .build();
  }

  private delete({gridApi, rowNode}: GridButtonClickedEvent): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.translate.instant(this.TRANSLATION_KEY).toLowerCase()}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(
        filter((result: AlertDialogResult) => result === AlertDialogResult.CONFIRM),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(() => {
        const rowsToRemove = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows() : [rowNode.data];
        this.configGridApi.applyTransaction({remove: rowsToRemove});

        this.setLatestMatchingCriteriaList();
      });
  }

  private onCellValueChanged(): void {
    this.setLatestMatchingCriteriaList();
    this.form.setErrors(document.querySelectorAll('.invalid').length > 0 ? {invalid: true} : null);
  }

  private getLatestMatchingCriteriaList(): MatchingCriteria[] {
    const updatedData: MatchingCriteria[] = [];
    this.configGridApi.forEachNode((node: IRowNode) => {
      if (node.data instanceof MatchingCriteria) {
        node.data.orderIndex = this.editPermission === Permission.TEXSTYLE_PLASTIC_PRODUCT_VIEW ? node.rowIndex : undefined;
      }

      updatedData.push(node.data);
    });
    return updatedData;
  }

  private setLatestMatchingCriteriaList(): void {
    const latestMatchingCriteriaList = this.getLatestMatchingCriteriaList();
    this.config.rowData.push(...latestMatchingCriteriaList);
    this.matchingCriteriaChange.emit(latestMatchingCriteriaList);
  }

  private setDisabledForOverlay(): void {
    this.noOverlayAction.forEach((param: OverlayComponentParamsAction) => (param.disabled = !this.canEdit));
  }
}
